import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ErrorSeverityType } from 'qp-store';
import { ContainerVm, ResourceVm } from 'qp-ui-interface';
import { NewrelicError } from 'src/app/analytics/newrelic/enums/errors';
import { ErrorFactory } from 'src/app/shared/modules/errors/handler/ErrorFactory';
import { NewrelicService } from '../../analytics/newrelic/service/newrelic.service';
import { ConfigService } from '../../shared/services/config/config.service';
import { RecommendationsService } from '../../shared/services/firstlight/recommendations/recommendations.service';
import { NavigationLibraryService } from '../../shared/services/navigation/navigation.service';
import { CarouselType } from '../../shared/shared-components/cards-carousel/model/cards-carousel-models';
import { getCarouselType, getShowPlayIcon, getShowProgress } from '../../shared/utils/cardsCarouselUtils';

@Component({
  selector: 'app-more-like-this',
  templateUrl: './more-like-this.component.html',
  styleUrls: ['./more-like-this.component.scss'],
})
export class MoreLikeThisComponent implements OnInit, OnDestroy {
  recommendationsContainerVM: ContainerVm;
  containersVM!: ContainerVm[];
  timeToShowRecommendations = 6;
  extraTimeAfterFocus = 3;
  private currentResource!: ResourceVm | undefined;
  private interval: any;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      id: string;
      resource: ResourceVm;
      tabId: string;
      useTimer: false;
    },
    private readonly dialogRef: MatDialogRef<MoreLikeThisComponent>,
    private readonly recommendationService: RecommendationsService,
    private readonly navService: NavigationLibraryService,
    private readonly newrelic: NewrelicService,
    private readonly configService: ConfigService
  ) {}

  ngOnInit(): void {
    if (this.data === null || this.data === undefined) {
      return;
    }

    this.currentResource = this.data.resource;
    this.fetchRecommedations();
    this.startTimer();
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  getCarouselType(container: ContainerVm): CarouselType {
    return getCarouselType(container);
  }

  getShowPlayIcon(container: ContainerVm): boolean {
    return getShowPlayIcon(container);
  }

  getShowProgress(container: ContainerVm): boolean {
    return getShowProgress(container);
  }

  /**
   * Give more time if focus is changed
   *
   * @param $event resourceId
   */
  focusChanged(): void {
    if (this.timeToShowRecommendations < this.extraTimeAfterFocus) {
      this.timeToShowRecommendations += this.extraTimeAfterFocus;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private startTimer(): void {
    if (!this.data.useTimer) {
      return;
    }

    this.interval = setInterval(() => {
      if (this.timeToShowRecommendations > 0) {
        this.timeToShowRecommendations--;
      } else {
        this.dialogRef.close();
      }
    }, 1000);
  }

  private stopTimer() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
  }

  private async fetchRecommedations(): Promise<void> {
    try {
      const recommendations = await this.recommendationService.fetchMoreLikeThis(
        this.currentResource.id,
        this.currentResource.contentType,
        this.currentResource.contentGenre,
        this.configService.applicationConfig.recommendationPlayerAspectRatio ?? '16x9',
        'player.moreLikeThis',
        'cvm-player-morelikethis'
      );
      this.recommendationsContainerVM = recommendations;
      this.navService.focusPage('focusable-container');
    } catch (error) {
      const err = ErrorFactory.get(error, NewrelicError.APP_ERROR);

      if ('error' in err) {
        this.newrelic.sendErrorEvent(
          NewrelicError.APP_ERROR,
          err.error.errorCode,
          `HTTP error: ${err.error.errorCode}`,
          error.message || 'Failed to fetch recommendations.',
          err,
          ErrorSeverityType.WARNING
        );
      }
      return Promise.reject(error);
    }
  }
}
